import {useEffect, useMemo, useState} from 'react'
import {Link, useNavigate, useParams, Routes, Route, useMatch} from 'react-router-dom'
import {useTranslation} from 'react-i18next'

import {KTSVG} from '../../../../../_metronic/helpers'
import {ListLands} from './ListLands'
import {useViewFieldContext} from './ViewFieldContext'
import {LoadingCard} from '../../../../../_metronic/elements/Loading'
import {ViewLand} from './ViewLand'
import {ListGaps} from './Gap/ListGaps'
import UpdateField from '../UpdateField'
import DeleteField from '../DeleteField'
import './ViewField.style.scss'
import {useMapLayersV2} from '../../Map/MapLayersV2Context'
import {Loading} from '../../../../../_metronic/elements/Loading'
import Land from './ListLands/Land'
import Gap from './Gap/ListGaps/Gap'
import {useIsMobile} from '../../../../shared/responsive'
import {formatHectares} from '../../../../shared/numberFormat'
import {useDrawV2} from '../../Map/Draw/DrawV2Context'

const FieldNotFound = () => {
  const {t} = useTranslation()
  return (
    <>
      <div className='card shadow-sm field-view-container'>
        <div className='card-body'>
          <h5 className='m-0'>{t('fields:errors.field_not_exists')}</h5>
        </div>
      </div>
    </>
  )
}

const NewLandButton = ({field, buttonStyle = false}) => {
  const {t} = useTranslation()
  const active = useMatch('/gis/field/view/:field_id/land/new')
  const isCreatingNoZoneAgro = useMatch('/gis/field/view/:field_id/gap/new')

  const attr = {}
  if (isCreatingNoZoneAgro) {
    attr['onClick'] = (e) => e.preventDefault()
  }

  return (
    <>
      <Link
        {...attr}
        to={`/gis/field/view/${field.field_id}/land/new`}
        className={
          'd-flex align-items-center rounded p-5 ' +
          (buttonStyle ? 'field-view-action-button ' : 'text-hover-primary field-view-action-link ') +
          (active ? 'bg-light-primary ' : '') +
          (isCreatingNoZoneAgro ? 'disabled-link' : '')
        }
      >
        <span className='field-view-action-link__plus' aria-hidden='true'>+</span>
        <div className='flex-grow-1 me-2 field-view-action-link__content'>
          <span className='fw-bold text-gray-800 fs-7 field-view-action-link__title'>
            {t('fields:buttons.add_lands')}
          </span>
        </div>
      </Link>
    </>
  )
}

const NewGapButton = ({field, buttonStyle = false}) => {
  const {t} = useTranslation()
  const active = useMatch('/gis/field/view/:field_id/gap/new')
  const isCreatingLand = useMatch('/gis/field/view/:field_id/land/new')

  const attr = {}
  if (isCreatingLand) {
    attr['onClick'] = (e) => e.preventDefault()
  }

  return (
    <>
      <Link
        {...attr}
        to={`/gis/field/view/${field.field_id}/gap/new`}
        className={
          'd-flex align-items-center rounded p-5 ' +
          (buttonStyle ? 'field-view-action-button ' : 'text-hover-primary field-view-action-link ') +
          (active ? 'bg-light-primary ' : '') +
          (isCreatingLand ? 'disabled-link' : '')
        }
      >
        <span className='field-view-action-link__plus' aria-hidden='true'>+</span>
        <div className='flex-grow-1 me-2 field-view-action-link__content'>
          <span className='fw-bold text-gray-800 fs-7 field-view-action-link__title'>
            {t('fields:buttons.add_no_agro_zone')}
          </span>
        </div>
      </Link>
    </>
  )
}

const ListLandsButton = ({field}) => {
  const {t} = useTranslation()
  const active = useMatch('/gis/field/view/:field_id/')

  return (
    <>
      <Link
        to={`/gis/field/view/${field.field_id}/`}
        className={
          'd-flex text-hover-primary align-items-center rounded p-5 field-view-action-link ' +
          (active ? 'bg-light-primary' : '')
        }
      >
        <KTSVG
          path='/media/icons/duotune/files/fil001.svg'
          className='svg-icon svg-icon-2x svg-icon-primary field-view-action-link__icon'
        />
        <div className='flex-grow-1 me-2 field-view-action-link__content'>
          <span className='fw-bold text-gray-800 fs-7 field-view-action-link__title'>
            {t('fields:buttons.lands')}
          </span>
        </div>
      </Link>
    </>
  )
}

const FieldMobileQuickAction = ({to, iconPath, label, disabled = false}) => {
  const attr = {}

  if (disabled) {
    attr['onClick'] = (event) => event.preventDefault()
  }

  return (
    <Link
      {...attr}
      to={to}
      className={`field-view-mobile-quick-action ${disabled ? 'disabled-link' : ''}`.trim()}
    >
      <KTSVG path={iconPath} className='svg-icon svg-icon-2 field-view-mobile-quick-action__icon' />
      <span className='field-view-mobile-quick-action__label'>{label}</span>
    </Link>
  )
}

const FieldMobileTabButton = ({active, label, meta, onClick}) => {
  return (
    <button
      type='button'
      className={`field-view-mobile-tab ${active ? 'active' : ''}`.trim()}
      onClick={onClick}
    >
      <span className='field-view-mobile-tab__label'>{label}</span>
      <span className='field-view-mobile-tab__meta'>{meta}</span>
    </button>
  )
}

const FieldMobileListPanel = ({loading, emptyLabel, children}) => {
  if (loading) {
    return (
      <div className='field-view-mobile-panel'>
        <div className='field-view-mobile-panel__loading'>
          <Loading />
        </div>
      </div>
    )
  }

  if (!children) {
    return (
      <div className='field-view-mobile-panel'>
        <div className='field-view-mobile-panel__empty'>{emptyLabel}</div>
      </div>
    )
  }

  return <div className='field-view-mobile-panel'>{children}</div>
}

const ViewField = () => {
  const {t} = useTranslation()
  const mapLayers = useMapLayersV2()
  const {field, loading} = useViewFieldContext()
  const {lands, loadingLands, gaps, loadingGaps} = useViewFieldContext()
  const [updateField, setUpdateField] = useState(false)
  const [deleteField, setDeleteField] = useState(false)
  const [mobileSection, setMobileSection] = useState('lands')
  const isMobile = useIsMobile()
  const draw = useDrawV2()
  const navigate = useNavigate()
  const viewingLand = useMatch('/gis/field/view/:field_id/land/view/:land_id/*')
  const creatingLand = useMatch('/gis/field/view/:field_id/land/new')
  const creatingGap = useMatch('/gis/field/view/:field_id/gap/new')
  const editingFieldRoute = useMatch('/gis/field/view/:field_id/update')
  const isRouteEditingField = editingFieldRoute != null
  const landsTotalHa = useMemo(() => {
    if (lands.length < 1) {
      return 0
    }

    return lands.reduce((total, currentLand) => total + parseFloat(currentLand.ha || 0), 0)
  }, [lands])
  const gapsTotalHa = useMemo(() => {
    if (gaps.length < 1) {
      return 0
    }

    return gaps.reduce((total, currentGap) => total + parseFloat(currentGap.ha || 0), 0)
  }, [gaps])

  useEffect(() => {
    setMobileSection('lands')
  }, [field?.field_id])

  if (loading) {
    return <LoadingCard className='field-view-container' />
  }

  if (!loading && field == null) {
    return <FieldNotFound />
  }

  const disableNewLand = Boolean(creatingGap)
  const disableNewGap = Boolean(creatingLand)
  const showMobileLists = isMobile && !creatingLand && !creatingGap && !viewingLand

  if (isMobile && viewingLand) {
    return (
      <>
        <div className='d-flex flex-column-auto mt-2'>
          <Routes>
            <Route path='/land/view/:land_id/*' element={<ViewLand />} />
          </Routes>
        </div>
      </>
    )
  }

  return (
    <>
      {isMobile ? (
        <>
          {!isRouteEditingField && !draw.isDrawing && (
            <div className='field-view-mobile-shell displayable'>
              <div className='card shadow-sm right-card-data field-view-mobile-card'>
                <div className='field-view-mobile-card__header'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light field-view-mobile-card__back'
                    onClick={() => navigate('/gis/field/list')}
                  >
                    <KTSVG
                      path='/media/icons/duotune/arrows/arr063.svg'
                      className='svg-icon svg-icon-2'
                    />
                    <span>{t('common:navigation.back_fields')}</span>
                  </button>
                  <div className='field-view-mobile-card__eyebrow'>{t('fields:labels.field')}</div>
                  <div className='field-view-mobile-card__title-row'>
                    <h2 className='field-view-mobile-card__title'>{field.name}</h2>
                    <div className='field-view-mobile-card__header-actions'>
                      <button
                        type='button'
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          mapLayers.fit(field.field_id)
                        }}
                        className='btn btn-icon btn-sm field-view-mobile-card__header-action'
                        aria-label={t('fields:buttons.focus_field')}
                        title={t('fields:buttons.focus_field')}
                      >
                        <KTSVG
                          path='/media/icons/duotune/maps/map007.svg'
                          className='svg-icon svg-icon-2'
                        />
                      </button>
                      <button
                        type='button'
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          navigate(`/gis/field/view/${field.field_id}/update`)
                        }}
                        className='btn btn-icon btn-sm field-view-mobile-card__header-action field-view-mobile-card__header-action--primary'
                        aria-label={t('fields:buttons.update')}
                        title={t('fields:buttons.update')}
                      >
                        <KTSVG
                          path='/media/icons/duotune/art/art005.svg'
                          className='svg-icon svg-icon-2'
                        />
                      </button>
                      <button
                        type='button'
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          setDeleteField(true)
                        }}
                        className='btn btn-icon btn-sm field-view-mobile-card__header-action field-view-mobile-card__header-action--danger'
                        aria-label={t('fields:buttons.delete')}
                        title={t('fields:buttons.delete')}
                      >
                        <KTSVG
                          path='/media/icons/duotune/general/gen027.svg'
                          className='svg-icon svg-icon-2'
                        />
                      </button>
                    </div>
                  </div>
                  <div className='field-view-mobile-card__stats'>
                    <div className='field-view-mobile-card__stat'>
                      <span className='field-view-mobile-card__stat-value'>{lands.length}</span>
                      <span className='field-view-mobile-card__stat-label'>Lotes</span>
                    </div>
                    <div className='field-view-mobile-card__stat'>
                      <span className='field-view-mobile-card__stat-value'>
                        {formatHectares(landsTotalHa)}
                      </span>
                      <span className='field-view-mobile-card__stat-label'>Productivas</span>
                    </div>
                    <div className='field-view-mobile-card__stat'>
                      <span className='field-view-mobile-card__stat-value'>{gaps.length}</span>
                      <span className='field-view-mobile-card__stat-label field-view-mobile-card__stat-label--compact'>
                        Zonas no agrícolas
                      </span>
                    </div>
                  </div>
                </div>

                <div className='field-view-mobile-card__quick-actions'>
                  <FieldMobileQuickAction
                    to={`/gis/field/view/${field.field_id}/land/new`}
                    iconPath='/media/icons/duotune/files/fil005.svg'
                    label={t('fields:buttons.add_lands')}
                    disabled={disableNewLand}
                  />
                  <FieldMobileQuickAction
                    to={`/gis/field/view/${field.field_id}/gap/new`}
                    iconPath='/media/icons/duotune/maps/map008.svg'
                    label={t('fields:buttons.add_no_agro_zone')}
                    disabled={disableNewGap}
                  />
                </div>

                {showMobileLists && (
                  <>
                    <div className='field-view-mobile-card__tabs'>
                      <FieldMobileTabButton
                        active={mobileSection === 'lands'}
                        label={t('lands:list.title')}
                        meta={`${lands.length} / ${formatHectares(landsTotalHa)}`}
                        onClick={() => setMobileSection('lands')}
                      />
                      <FieldMobileTabButton
                        active={mobileSection === 'gaps'}
                        label={t('gaps:list.title')}
                        meta={`${gaps.length} / ${formatHectares(gapsTotalHa)}`}
                        onClick={() => setMobileSection('gaps')}
                      />
                    </div>

                    {mobileSection === 'lands' && (
                      <FieldMobileListPanel
                        loading={loadingLands}
                        emptyLabel={t('lands:list.empty_lands')}
                      >
                        {!loadingLands &&
                          lands.length > 0 &&
                          lands.map((land) => {
                            return <Land key={land.land_id} land={land} field={field} />
                          })}
                      </FieldMobileListPanel>
                    )}

                    {mobileSection === 'gaps' && (
                      <FieldMobileListPanel loading={loadingGaps} emptyLabel={t('gaps:list.empty')}>
                        {!loadingGaps &&
                          gaps.length > 0 &&
                          gaps.map((gap) => {
                            return <Gap key={gap.gap_id} gap={gap} field={field} />
                          })}
                      </FieldMobileListPanel>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          <UpdateField open={isRouteEditingField && !draw.isDrawing} setOpen={setUpdateField} />
        </>
      ) : (
        <>
          <div className='d-flex flex-column-auto displayable'>
            <div className='card shadow-sm field-view-container right-card-data field-view-card'>
              <div
                className='card-header field-view-card__header'
              >
                <div className='field-view-card__title-group'>
                  <span className='field-view-card__title'>{field.name}</span>
                  <span className='field-view-card__meta'>
                    {formatHectares(landsTotalHa)}
                  </span>
                </div>

                <div
                  className='card-toolbar field-view-card__toolbar'
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <button
                    type='button'
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      mapLayers.fit(field.field_id)
                    }}
                    className='btn btn-color-muted btn-active-color-primary btn-sm field-view-card__focus'
                  >
                    <KTSVG
                      path='/media/icons/duotune/maps/map007.svg'
                      className='svg-icon svg-icon-1 field-view-card__focus-icon'
                    />
                  </button>

                  <button
                    type='button'
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      setDeleteField(true)
                    }}
                    className='btn btn-sm btn-light-danger field-view-card__action-button'
                  >
                    {t('fields:buttons.delete')}
                  </button>
                  <button
                    type='button'
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      setUpdateField(true)
                    }}
                    className='btn btn-sm btn-light-primary field-view-card__action-button'
                  >
                    {t('fields:buttons.update')}
                  </button>
                </div>
              </div>
              <div className='field-view-card__content'>
                <div className='card-body field-view-card__body'>
                  <ListLandsButton field={field} />
                  <div className='field-view-action-button-grid'>
                    <NewLandButton field={field} buttonStyle />
                    <NewGapButton field={field} buttonStyle />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <div className='d-flex flex-column-auto mt-4'>
        {!isMobile && <UpdateField open={updateField} setOpen={setUpdateField} />}
        <DeleteField open={deleteField} setOpen={setDeleteField} />
        <Routes>
          {!isMobile && <Route path='/' element={<ListLands />} />}
          <Route path='/land/view/:land_id/*' element={<ViewLand />} />
        </Routes>
      </div>
      {!isMobile && (
        <div className='d-flex flex-column-auto mt-4'>
          <Routes>
            <Route path='/' element={<ListGaps />} />
          </Routes>
        </div>
      )}
    </>
  )
}

const ViewFieldContainer = () => {
  const viewFieldContext = useViewFieldContext()
  const {field_id} = useParams()

  useEffect(() => {
    viewFieldContext.setFieldId(field_id)
  }, [field_id])

  return (
    <>
      <div className='d-flex flex-column flex-row-auto field-view-flex-container'>
        <ViewField />
      </div>
    </>
  )
}

export default ViewFieldContainer
